<template>
    <div class="tls-root">
        <!-- Toolbar -->
        <div class="tls-toolbar">
            <span class="tls-guide">
                {{ $t('업데이트 버튼을 누르면 최신 결과로 갱신됩니다.') }}
                <span v-if="lastFetchedAt" class="tls-fetched-at">({{ $t('{time} 기준', {time: lastFetchedAt}) }})</span>
            </span>
            <button class="tls-refresh-btn" :disabled="loading" @click="refresh">
                <fa icon="sync-alt" :spin="loading" class="mr-1" />
                {{ $t('경영 수준 진단 결과 업데이트') }}
            </button>
        </div>

        <div class="tls-wrap" role="table" :aria-label="$t('테마 평가 수준')">
        <!-- Header -->
        <div class="tls-header" role="row">
            <div v-for="col in levelColumns"
                 :key="col.key"
                 class="tls-level"
                 role="columnheader"
                 :style="{ '--accent': col.accent, backgroundColor: col.bg }"
            >
                {{ col.label }}
            </div>
        </div>

        <!-- Loading: 이미 그린 표가 있으면 그대로 두고(버튼 아이콘만 돈다) 처음 불러올 때만 자리를 차지한다 -->
        <div v-if="loading && rows.length === 0" class="tls-loading" role="status">
            <b-spinner small class="mr-2"></b-spinner>
            <span>{{ $t('평가 수준 분석 중') }}...</span>
        </div>

        <!-- Empty -->
        <div v-else-if="rows.length === 0" class="tls-empty">
            <span class="text-muted">{{ $t('문항에 답변하면 평가 수준이 표시됩니다.') }}</span>
        </div>

        <!-- Body grid (column-packed) -->
        <div v-else class="tls-body" role="rowgroup">
            <div v-for="(gridRow, rIdx) in gridRows" :key="rIdx" class="tls-row" role="row">
                <div v-for="(item, cIdx) in gridRow"
                     :key="LEVEL_KEYS[cIdx] + '-' + rIdx"
                     class="tls-cell"
                     role="cell"
                     :class="item ? `tls-cell--filled tls-cell--${LEVEL_KEYS[cIdx]}` : 'tls-cell--empty'"
                >
                    <template v-if="item">
                        <span class="tls-chip-label">{{ item.label }}</span>
                        <span class="tls-chip-name">{{ item.name }}</span>
                    </template>
                </div>
            </div>
        </div>
        </div>
        <!-- 답하지 않은 테마 문항은 표에 칸이 없으므로 따로 알린다 -->
        <div v-if="!loading && unansweredLabels.length" class="tls-note">
            {{ $t('미답변') }}: {{ unansweredLabels.join(', ') }}
        </div>
    </div>
</template>

<script>
import managementLevelConfig from '@shared/mixin/report/raw-data/theme-management-level-config.json';
import themeSubtopics from '@shared/mixin/report/raw-data/theme-subtopics.json';
import assessmentConstants from '@shared/mixin/report/raw-data/theme-assessment-constants.json';

const LEVEL_KEYS = ['na', 'insufficient', 'average', 'excellent', 'outstanding'];

const LEVEL_TONES = {
    na:           { bg: '#f2f2f2', accent: '#bbb' },
    insufficient: { bg: '#ffcccc', accent: '#e06060' },
    average:      { bg: '#ffff99', accent: '#c8b900' },
    excellent:    { bg: '#c1f0c8', accent: '#4caf50' },
    outstanding:  { bg: '#bde0fe', accent: '#1971c2' },
};

export default {
    name: 'ThemeLevelSummary',
    props: {
        scoringData: { type: Array, default: null },
        subsData:    { type: Array, default: null },
        companyId:   { type: [String, Number], default: null },
        campaignId:  { type: [String, Number], default: null },
        projectCode: { type: String, default: null },
        subCodes:    { type: Array, default: () => [] },
        themeFilter: { type: String, default: null }, // 'water' | 'biodiversity' | null
    },
    data() {
        return {
            LEVEL_KEYS,
            fetchedItems: [],
            loading: false,
            lastFetchedAt: null,
        };
    },
    computed: {
        rawScoringData() {
            return this.scoringData || this.fetchedItems;
        },
        levelLabels() {
            return assessmentConstants?.levelSchema?.labels || {
                na: '해당 없음',
                insufficient: '미흡',
                average: '보통',
                excellent: '우수',
                outstanding: '매우 우수',
            };
        },
        levelColumns() {
            return LEVEL_KEYS.map(key => ({
                key,
                label: this.$t(this.levelLabels[key]),
                bg: LEVEL_TONES[key].bg,
                accent: LEVEL_TONES[key].accent,
            }));
        },
        // sub_code 단위 그룹핑 (unanswered: 표에서 빠진 미답변 문항 label)
        tableData() {
            const raw = this.rawScoringData;
            if (!raw || raw.length === 0) return { items: [], unanswered: [] };

            const subsMap = {};
            (this.subsData || []).forEach(s => {
                if (s && s.sub_code) subsMap[s.sub_code] = s;
            });

            const grouped = {};
            raw.forEach(r => {
                const sc = r.sub_code;
                if (!sc) return;
                if (!grouped[sc]) grouped[sc] = { sub_code: sc, rows: [] };
                grouped[sc].rows.push(r);
            });

            const items = [];
            const unansweredItems = [];
            Object.values(grouped).forEach(group => {
                const meta = themeSubtopics.byCode?.[group.sub_code];
                if (!meta) return; // 테마평가 sub_code 화이트리스트 외 제외
                if (this.themeFilter && meta.theme !== this.themeFilter) return;

                const subData = subsMap[group.sub_code];
                const result = subData?.sub_data_contents?.result;
                const isEmptyResult = !result
                    || result === ''
                    || (Array.isArray(result) && result.length === 0);

                const allNotApplicable = group.rows.length > 0
                    && group.rows.every(r => r.not_applicable === true);
                const score = group.rows.reduce((sum, r) =>
                    sum + (r.not_applicable ? 0 : (r.score || 0)), 0);
                const unanswered = !allNotApplicable && isEmptyResult;

                if (unanswered) { // 미답변은 표에서 제외하고 아래 안내에 모은다
                    unansweredItems.push({ sub_code: group.sub_code, label: meta.label });
                    return;
                }

                const level = this.computeLevel(meta.label, score, allNotApplicable);

                items.push({
                    sub_code: group.sub_code,
                    label: meta.label,
                    name: this.showTranslation(meta.name, meta.name_e, meta.name_c),
                    theme: meta.theme,
                    score,
                    level,
                });
            });

            // sub_code 자연 정렬: TH1.* 먼저, 같은 prefix 내에서는 숫자 순
            return {
                items: items.sort(this.compareSubCode),
                unanswered: unansweredItems.sort(this.compareSubCode).map(({ label }) => label),
            };
        },
        rows() {
            return this.tableData.items;
        },
        unansweredLabels() {
            return this.tableData.unanswered;
        },
        // 컬럼별 칩을 packed 방식으로 배치 — 행 수 = max(컬럼별 항목 수)
        // ex) na=1 / minor=1 / outstanding=1 → 1 row
        //     minor=1 / outstanding=2 → 2 row
        gridRows() {
            const byLevel = {};
            LEVEL_KEYS.forEach(k => { byLevel[k] = []; });
            this.rows.forEach(item => {
                if (byLevel[item.level]) byLevel[item.level].push(item);
            });
            const rowCount = Math.max(0, ...LEVEL_KEYS.map(k => byLevel[k].length));
            const grid = [];
            for (let r = 0; r < rowCount; r++) {
                grid.push(LEVEL_KEYS.map(k => byLevel[k][r] || null));
            }
            return grid;
        },
    },
    watch: {
        subCodes: {
            handler(newVal, oldVal) {
                // 답변 저장마다 부모가 같은 목록을 새 배열로 내려준다 — 문항 구성이 같으면 다시 부르지 않는다 (갱신은 업데이트 버튼)
                if (oldVal && newVal && newVal.join(',') === oldVal.join(',')) return;
                if (this.scoringData === null && newVal && newVal.length > 0) {
                    this.fetchScoring();
                }
            },
            immediate: true,
        },
        campaignId() {
            if (this.scoringData === null && this.subCodes.length > 0) this.fetchScoring();
        },
    },
    methods: {
        // 결과보고서와 같은 규칙 (report.hmc_theme.mixin.js htDeriveLevel: 배점 대비 비율 0.15 / 0.5 / 0.85).
        // 설정의 점수 구간은 한 점씩이라, 중간 점수가 '미흡' 으로 떨어지던 것을 막는다 (설정의 모든 점은 이 규칙과 같은 등급)
        computeLevel(label, score, allNotApplicable) {
            if (allNotApplicable) return 'na';
            const cap = Number(managementLevelConfig[label]?.scoreRange?.[1] ?? 0);
            const r = cap > 0 ? score / cap : 0;
            if (r < 0.15) return 'insufficient';
            if (r < 0.5) return 'average';
            if (r < 0.85) return 'excellent';
            return 'outstanding';
        },
        compareSubCode(a, b) {
            const parse = (sc) => {
                const m = sc.match(/^TH(\d+)\.(\d+)$/);
                return m ? [Number(m[1]), Number(m[2])] : [99, 99];
            };
            const [pa, na] = parse(a.sub_code);
            const [pb, nb] = parse(b.sub_code);
            return pa - pb || na - nb;
        },
        refresh() {
            if (this.scoringData !== null) {
                this.$emit('refresh');
            } else {
                this.fetchScoring();
            }
        },
        async fetchScoring() {
            if (!this.companyId || !this.campaignId || !this.projectCode || this.subCodes.length === 0) return;

            // 늦게 온 앞 요청 응답은 버린다 (최신 요청만 반영)
            const seq = this._fetchSeq = (this._fetchSeq || 0) + 1;
            this.loading = true;
            try {
                const res = await this.$axios.get('/v2/scoring', {
                    params: {
                        company_id: this.companyId,
                        campaign_id: this.campaignId,
                        project_code: this.projectCode,
                        sub_codes: this.subCodes.join(','),
                        live: true,
                    },
                });
                const data = res.data;
                let raw = [];
                if (data?.scoring?.scoring_pd) raw = raw.concat(data.scoring.scoring_pd);
                if (data?.risk?.risk_pd) raw = raw.concat(data.risk.risk_pd);
                if (seq !== this._fetchSeq) return;
                this.fetchedItems = raw;
                const pad = n => String(n).padStart(2, '0');
                const now = new Date();
                this.lastFetchedAt = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
            } catch (e) {
                if (seq !== this._fetchSeq) return;
                console.error('ThemeLevelSummary scoring fetch error:', e?.response?.status ?? e?.message);
                this.fetchedItems = [];
            } finally {
                if (seq === this._fetchSeq) this.loading = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.tls-root {
    width: 100%;
}

.tls-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
}

.tls-fetched-at {
    color: #6b6b6b;
    white-space: nowrap;
}

.tls-note {
    margin-top: 6px;
    font-size: 0.82rem;
    color: #5d4037;
}

@media (max-width: 575.98px) {
    .tls-level {
        padding: 8px 2px;
        font-size: 0.74rem;
    }
}

.tls-guide {
    flex: 1;
    min-width: 0;
    font-size: 0.82rem;
    line-height: 1.45;
    color: #5d4037;
    word-break: keep-all;
}

.tls-refresh-btn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    background: #fff;
    color: #2c2c2c;
    border: 1px solid #c4c4c4;
    border-radius: 4px;
    font-size: 0.83rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;

    &:hover:not(:disabled) {
        background: #f4f4f4;
        border-color: #888;
        color: #111;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.55;
    }
}

.tls-wrap {
    border: 1px solid #d0d0d0;
    border-radius: 6px;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

// 본문 행과 같은 5칸 격자 — 최소 폭을 두면 좁은 화면에서 머리글만 잘려 칸이 어긋난다
.tls-header {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
}

.tls-level {
    min-width: 0;
    word-break: keep-all;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 8px;
    font-size: 0.88rem;
    font-weight: 700;
    color: #111;
    border-right: 1px solid rgba(255, 255, 255, 0.6);
    position: relative;

    &:last-of-type {
        border-right: none;
    }

    &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: var(--accent, #333);
    }
}

.tls-loading,
.tls-empty {
    padding: 16px;
    text-align: center;
    font-size: 0.85rem;
    color: #6b6b6b;
    background: #f8f8f8;
    border-top: 1px solid #eee;
}

.tls-body {
    background: #f8f9fa;
    border-top: 1px solid #e8e8e8;
}

.tls-row {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    border-bottom: 1px solid #eaeaea;

    &:last-child {
        border-bottom: none;
    }
}

.tls-cell {
    padding: 10px 12px;
    border-right: 1px solid #eaeaea;
    min-height: 44px;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.86rem;
    line-height: 1.4;

    &:last-child {
        border-right: none;
    }
}

.tls-cell--empty {
    background: transparent;
}

.tls-cell--filled {
    font-weight: 500;
    color: #1f1f1f;
}

.tls-cell--na           { background: #f5f5f5; color: #707070; }
.tls-cell--insufficient { background: #fff0f0; }
.tls-cell--average      { background: #fffce0; }
.tls-cell--excellent    { background: #e6f7e8; }
.tls-cell--outstanding  { background: #e3f0fc; }

.tls-chip-label {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.74rem;
    font-weight: 700;
    background: #fff;
    border: 1px solid currentColor;
    color: #333;
    letter-spacing: 0.02em;
}

.tls-chip-name {
    word-break: keep-all;
    overflow-wrap: anywhere;
    min-width: 0;
    color: #2c2c2c;
}
</style>
